'use client'

import { useEffect, useMemo, useRef, useState } from 'react'

import { Button } from '@/app/components/atoms/Button'
import { Chip } from '@/app/components/atoms/Chip'
import { IconFilter } from '@/app/components/atoms/Icons'
import { VistaToggle } from '@/app/components/atoms/VistaToggle'
import type { ViewMode } from '@/app/components/atoms/VistaToggle'
import { SelectField } from '@/app/components/shared/forms/SelectField'
import { SideSheet } from '@/app/components/atoms/SideSheet'
import { useOptionalPageAnalytics } from '@/app/context/analytics'
import { useViewModeContext } from '@/app/context/viewMode'

import { AppliedFilterChips } from './AppliedFilterChips'
import { buildActiveFilterChips } from './buildActiveFilterChips'
import { RangeDropdownField } from './RangeDropdownField'
import styles from './ListingFilters.module.scss'
import type { ListingDateChipField, ListingFiltersAnalytics, ListingFiltersProps } from './types'

/**
 * Chips de Fecha (Hoy/Finde/Mes…, backend `date_range.items`): selección
 * única, aplicación instantánea. La chip activa se muestra `removable` (con
 * botón de cierre ✕) para desmarcarla explícitamente y volver a "cualquier
 * fecha"; pulsar otra la reemplaza directamente.
 *
 * `inverse`: sobre banda oscura (comportamiento histórico) o sobre fondo
 * claro (`ListingFiltersProps.inverse`, ver su JSDoc) — el llamador decide
 * cuál according a si esta instancia concreta es la fila inline (sigue el
 * fondo de la página) o la de la franja sticky (siempre oscura, propio fondo).
 */
function DateChips({
  dateField,
  inverse,
  className,
}: {
  dateField: ListingDateChipField
  inverse: boolean
  className?: string
}) {
  if (dateField.options.length === 0) return null
  return (
    <ul className={className} aria-label={dateField.label}>
      {dateField.options.map((option) => {
        const isSelected = option.value === dateField.value
        return (
          <li key={option.value} className={styles.dateChipItem}>
            {isSelected && option.value === dateField.defaultValue ? (
              <Chip label={option.label} selected inverse={inverse} />
            ) : isSelected ? (
              <Chip
                label={option.label}
                selected
                inverse={inverse}
                removable
                onRemove={() => dateField.onChange('')}
              />
            ) : (
              <Chip
                label={option.label}
                disabled={option.disabled}
                inverse={inverse}
                onClick={() => dateField.onChange(option.value)}
              />
            )}
          </li>
        )
      })}
    </ul>
  )
}

/** Alto de header de respaldo (mobile) si `--header-bottom` aún no se ha publicado. */
const FALLBACK_HEADER_BOTTOM_PX = 124

const SHEET_TITLE = 'Filtros'

/**
 * `id` del `<section>` de Newsletter (`NewsletterForm.tsx`) que el layout raíz
 * de `(general-web)` monta al final de TODA página (justo antes del Footer —
 * ver `layout.tsx`'s `SiteFooter`), con independencia del listado concreto
 * que use `ListingFilters`. Se detecta por `id` (no por un `endRef` pasado
 * desde la página, como en la referencia) porque esa sección vive fuera del
 * árbol de la página, en un Server Component hermano — no hay ref de React
 * posible entre ambos. Franja sticky del DS de referencia: se oculta al
 * sobrepasar el final del listado para no montarse sobre la Newsletter.
 */
const NEWSLETTER_SECTION_ID = 'newsletter-section'

/**
 * Consume `ViewModeContext` solo cuando `showViewToggle` monta este
 * subcomponente — así el hook (que exige `<ViewModeProvider>` como ancestro)
 * nunca se llama en páginas que no activan el selector de vista, sin violar
 * las reglas de hooks condicionales (specs/019-estados-compra-eventos).
 *
 * `className` (`.viewToggle`/`.stickyToggle`, con su propio `display: none`
 * por debajo de desktop) se aplica a un `<div>` envolvente, NO directamente a
 * `VistaToggle` — su `.root` fija `display: inline-flex` sin condición de
 * breakpoint, y al vivir en un CSS Module distinto, ambas clases tienen la
 * misma especificidad: cuál gana depende del orden del bundle, no de la
 * intención del código, así que la ocultación en mobile/tablet dejaba de
 * cumplirse en la práctica.
 */
function ViewToggleControl({
  className,
  inverse,
  analytics,
}: {
  className?: string
  inverse: boolean
  analytics?: ListingFiltersAnalytics
}) {
  const { viewMode, setViewMode } = useViewModeContext()
  const pageAnalytics = useOptionalPageAnalytics()

  // `ui_interaction` del toggle de vista (`Listado filtrado.csv` → «Toggle
  // VISTA grid / list»). El diccionario nombra las disposiciones `grid`/`list`,
  // no `vertical`/`horizontal` como el DS. `module_position=no_aplica`: el
  // toggle no es un módulo de producto, solo un control de la franja. Sin
  // `analytics` (página aún sin instrumentar) o fuera del provider (tests), no
  // se emite nada y el toggle sigue funcionando igual.
  function handleChange(next: ViewMode) {
    if (analytics) {
      pageAnalytics?.trackUiInteraction({
        component_type: 'tab',
        component_action: 'select',
        component_id: `${analytics.pageType}_view_toggle`,
        component_label: 'vista',
        selected_value: next === 'vertical' ? 'grid' : 'list',
        module_name: analytics.moduleName,
        module_type: analytics.moduleType,
        module_position: 'no_aplica',
      })
    }
    setViewMode(next)
  }

  // `inverse`: histórico esta fila (y siempre la franja sticky) vivían sobre
  // la banda oscura de `topRegion` — ahora configurable por
  // `ListingFiltersProps.inverse` para la instancia inline (ver su JSDoc); la
  // instancia de la franja sticky sigue forzando `true` siempre (fondo propio
  // oscuro, ver comentario de `.stickyControlsRow button` más abajo).
  return (
    <div className={className}>
      <VistaToggle value={viewMode} onChange={handleChange} inverse={inverse} />
    </div>
  )
}

/**
 * `ListingFilters` — franja de filtros de un listado (DS): en desktop muestra
 * los dropdowns; en mobile/tablet los colapsa a un botón "Ordenar y filtrar"
 * que abre un `SideSheet` con los mismos campos en modo borrador
 * (Aplicar/Limpiar). Genérico y reutilizable por cualquier página con su
 * propio conjunto de filtros (no conoce nombres de filtro concretos, solo
 * `fields`/`instantField` que le pasa la página consumidora) — mismo
 * principio que `useInfiniteList`/`VirtualizedList`.
 *
 * Al hacer scroll y sobrepasar esta fila, una franja compacta con el mismo
 * botón queda fija bajo el header (`--header-bottom`, publicado por
 * `Header.tsx` — ya cuenta el buscador integrado).
 */
export function ListingFilters({
  instantField,
  dateField,
  rangeField,
  fields,
  totalResults,
  clearValues,
  onApply,
  showViewToggle = false,
  clearAllHref,
  onClearAll,
  analytics,
  inverse = true,
  stickyBar = true,
}: ListingFiltersProps) {
  const [isSheetOpen, setSheetOpen] = useState(false)
  const [draft, setDraft] = useState<string[]>(() => fields.map((field) => field.value))
  const [isPinned, setPinned] = useState(false)
  const sentinelRef = useRef<HTMLDivElement>(null)

  // Chips de "filtros aplicados": derivados de los mismos campos que ya pinta
  // la franja (URL como fuente de verdad). Fecha se excluye (ya tiene su propia
  // fila de chips) y "Ordenar por" también (campos con `chip: false`).
  const appliedChips = useMemo(
    () => buildActiveFilterChips({ instantField, fields, rangeField }),
    [instantField, fields, rangeField],
  )

  // La franja sticky aparece al sobrepasar esta fila de filtros y se oculta de
  // nuevo al sobrepasar el final del listado (la sección de Newsletter que
  // sigue a cualquier página — ver `NEWSLETTER_SECTION_ID`), para no quedar
  // montada sobre ella. Se mide en cada frame de scroll (no con
  // IntersectionObserver) para reaccionar igual ante un scroll continuo que
  // ante un salto instantáneo (deep-link con `#hash` o "volver" del
  // navegador). El umbral es `--header-bottom`, no una altura fija: si el
  // header cambia de alto (p. ej. buscador integrado), el umbral se mueve con él.
  //
  // Con `stickyBar={false}` (p. ej. el módulo Agenda de canal, que no es la
  // única franja de filtrado de una página más larga) ni se monta el
  // listener de scroll ni se calcula `isPinned` — la franja sticky no existe
  // en absoluto para esta instancia.
  useEffect(() => {
    if (!stickyBar) return undefined
    const sentinel = sentinelRef.current
    if (!sentinel) return undefined

    let frame = 0
    const update = () => {
      frame = 0
      const headerBottom =
        parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue('--header-bottom'),
        ) || FALLBACK_HEADER_BOTTOM_PX
      const pastStart = sentinel.getBoundingClientRect().top < headerBottom
      const newsletterSection = document.getElementById(NEWSLETTER_SECTION_ID)
      const pastEnd = newsletterSection
        ? newsletterSection.getBoundingClientRect().top < headerBottom
        : false
      setPinned(pastStart && !pastEnd)
    }
    const onScrollOrResize = () => {
      if (!frame) frame = requestAnimationFrame(update)
    }

    update()
    window.addEventListener('scroll', onScrollOrResize, { passive: true })
    window.addEventListener('resize', onScrollOrResize)
    return () => {
      if (frame) cancelAnimationFrame(frame)
      window.removeEventListener('scroll', onScrollOrResize)
      window.removeEventListener('resize', onScrollOrResize)
    }
  }, [stickyBar])

  function openSheet() {
    setDraft(fields.map((field) => field.value))
    setSheetOpen(true)
  }

  function applyDraft() {
    onApply(draft)
    setSheetOpen(false)
  }

  function clearDraft() {
    setDraft([...clearValues])
    rangeField?.onClear?.()
  }

  function updateDraftAt(index: number, value: string) {
    setDraft((current) => current.map((current_, i) => (i === index ? value : current_)))
  }

  const triggerButton = (
    <Button
      type="button"
      variant="ghost"
      size="s"
      leadingIcon={<IconFilter size={16} />}
      onClick={openSheet}
    >
      Ordenar y filtrar
    </Button>
  )

  return (
    <div className={styles.root} data-inverse={inverse ? 'true' : 'false'}>
      <div className={styles.controls}>
        <div className={styles.inlineFields}>
          {instantField && (
            <div style={instantField.maxWidth ? { maxWidth: instantField.maxWidth } : undefined}>
              <SelectField
                label={instantField.label}
                value={instantField.value}
                options={instantField.options}
                placeholder={instantField.placeholder}
                clearable={instantField.clearable}
                emptyValue={instantField.emptyValue}
                disabled={instantField.disabled}
                onChange={instantField.onChange}
                variant={inverse ? 'inverse' : undefined}
              />
            </div>
          )}
          {fields.map((field) => (
            <div key={field.label} style={field.maxWidth ? { maxWidth: field.maxWidth } : undefined}>
              <SelectField
                label={field.label}
                value={field.value}
                options={field.options}
                placeholder={field.placeholder}
                clearable={field.clearable}
                emptyValue={field.emptyValue}
                disabled={field.disabled}
                onChange={field.onChange}
                variant={inverse ? 'inverse' : undefined}
              />
            </div>
          ))}
          {rangeField && (
            <RangeDropdownField rangeField={rangeField} variant={inverse ? 'inverse' : undefined} />
          )}
        </div>

        {/* Fila(s) debajo de los dropdowns (paridad con la referencia): las chips de
            Fecha sugeridas (si la página las pasa) ocupan su propia línea. Los chips
            de filtros aplicados van SIEMPRE a continuación de la Fecha: inline en la
            misma línea desde `desktop-medium` (≥1200px), y por debajo (fila que
            envuelve) en resoluciones menores, compartiendo esa fila con el botón
            "Ordenar y filtrar" (mobile/tablet) o el selector de vista (desktop). */}
        <div className={styles.switchRow}>
          {dateField && <DateChips dateField={dateField} inverse={inverse} className={styles.dateChips} />}
          {/* Chips aplicados + control agrupados en una fila que NO envuelve:
              así, cuando el espacio aprieta (mobile), los chips hacen scroll
              horizontal y el control ("Ordenar y filtrar"/vista) permanece a su
              lado en la misma línea, en vez de saltar a una línea propia. */}
          <div className={styles.chipsAndActions}>
            <AppliedFilterChips
              chips={appliedChips}
              clearAllHref={clearAllHref}
              onClearAll={onClearAll}
              inverse={inverse}
            />
            <div className={styles.actionsRow}>
              <div className={styles.mobileTrigger}>{triggerButton}</div>
              {showViewToggle && (
                <ViewToggleControl className={styles.viewToggle} inverse={inverse} analytics={analytics} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* `stickyBar={false}` (p. ej. el módulo Agenda de canal): ni el
          sentinela ni la franja sticky se montan — solo la fila inline de
          arriba, siempre en su sitio, nunca fija bajo el header al hacer
          scroll por el resto de la página. */}
      {stickyBar && (
        <>
          {/* Sentinela invisible: marca el final de esta fila de filtros para
              saber cuándo la franja sticky debe engancharse bajo el header. */}
          <div ref={sentinelRef} className={styles.sentinel} aria-hidden="true" />

          {/* Se mantiene montada (para poder animar el cierre) y se anima vía
              `transform`/`opacity` en vez de desmontarse de golpe. `inert` (en vez de desmontar o solo
              `aria-hidden`) saca el bloque del foco/tabulación y de la accesibility
              tree mientras está oculta, sin necesidad de gestionar el foco a mano. */}
          <div
            className={styles.stickyBar}
            data-hidden={!isPinned ? '' : undefined}
            inert={!isPinned}
            role="region"
            aria-label="Ordenar y filtrar"
          >
            <div className={styles.stickyBarInner}>
              {/* Franja sticky: SIEMPRE oscura (fondo propio, `.stickyBar`), con
                  independencia de `inverse` — nunca se pone en modo claro. */}
              {dateField && <DateChips dateField={dateField} inverse className={styles.dateChips} />}
              <div className={styles.stickyControlsRow}>
                {showViewToggle && (
                  <ViewToggleControl className={styles.stickyToggle} inverse analytics={analytics} />
                )}
                {triggerButton}
              </div>
            </div>
          </div>
        </>
      )}

      <SideSheet
        open={isSheetOpen}
        onClose={() => setSheetOpen(false)}
        title={SHEET_TITLE}
        size="s"
        footer={
          <div className={styles.sheetFooter}>
            <Button type="button" variant="filled" stretch onClick={applyDraft}>
              Aplicar filtros ({totalResults})
            </Button>
            <Button type="button" variant="outlined" stretch onClick={clearDraft}>
              Limpiar filtros
            </Button>
          </div>
        }
      >
        <div className={styles.sheetFields}>
          {/* El campo instantáneo navega al instante (igual que en desktop) —
              no forma parte del borrador de "Aplicar filtros". */}
          {instantField && (
            <SelectField
              label={instantField.label}
              value={instantField.value}
              options={instantField.options}
              placeholder={instantField.placeholder}
              clearable={instantField.clearable}
              emptyValue={instantField.emptyValue}
              disabled={instantField.disabled}
              onChange={instantField.onChange}
            />
          )}
          {fields.map((field, index) => (
            <SelectField
              key={field.label}
              label={field.label}
              value={draft[index] ?? field.value}
              options={field.options}
              placeholder={field.placeholder}
              clearable={field.clearable}
              emptyValue={field.emptyValue}
              disabled={field.disabled}
              onChange={(value) => updateDraftAt(index, value)}
            />
          ))}
          {/* Aplicación instantánea (igual que `instantField` arriba): el rango
              no forma parte del borrador de "Aplicar filtros". */}
          {rangeField && <RangeDropdownField rangeField={rangeField} />}
        </div>
      </SideSheet>
    </div>
  )
}
